import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ComponentType, FC, memo } from 'react';
import BlackFridayOpener from 'shared/assets/images/BlackFridayOpener.png';
import ChristmasOpener from 'shared/assets/images/ChristmasOpener.png';
import EasterPromotionOpener from 'shared/assets/images/EasterPromotionOpener.png';
import NewYearOpener from 'shared/assets/images/NewYearOpener.png';
import ValentinesDayOpener from 'shared/assets/images/ValentinesDayOpener.png';
import { BlackFridayPromotionContent } from 'widgets/BlackFridayPromotion';
import { ChristmasPromotionContent } from 'widgets/ChristmasPromotion';
import { CleverFoxPromotionContent } from 'widgets/CleverFoxPromotion';
import { EasterPromotionContent } from 'widgets/EasterPromotion';
import { NewYearPromotionContent } from 'widgets/NewYearPromotion';
import { ValentinesDayPromotionContent } from 'widgets/ValentinesDayPromotion';
import { PromotionEventTheme } from '../model/types';
import { useSpecialPromotionModal } from '../utils/hooks/useSpecialPromotionModal';
import { ChristmasAndNewYearPromotionContent } from 'widgets/ChristmasAndNewYearPromotion';
import { NewWarehousePromotionContent as NewSwissboxWarehousePromotionContent } from 'widgets/NewWarehouseSwissboxPromotion';
import { getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getEventThemeByClient } from 'shared/utils/helpers/getEventThemeByClient';
import { NewWarehousePromotionContent } from 'widgets/NewWarehousePromotion';

interface EventThemeParams {
  background: string;
  label: string;
  labelFont: string;
  Content: ComponentType;
  image?: string;
  date: string;
  dateColor: string;
}

export const SpecialPromotionOpener: FC = memo(() => {
  const { t } = useAppTranslation('common');

  const { openModal, showSpecialPromotion } = useSpecialPromotionModal();
  const globalSettings = useAppSelector(getGlobalSettings);

  const eventTheme = getEventThemeByClient(globalSettings?.clientName) as PromotionEventTheme;
  console.log('eventTheme', eventTheme);

  const eventThemeMap: Record<PromotionEventTheme, EventThemeParams> = {
    default: {
      background: 'bg-gradient-to-r from-[#636363] to-[#252425]',
      label: 'Black Friday Sale',
      labelFont: 'font-sans',
      Content: BlackFridayPromotionContent,
      date: '11.11',
      image: BlackFridayOpener,
      dateColor: 'text-primary',
    },
    blackFriday: {
      background: 'bg-gradient-to-r from-[#636363] to-[#252425]',
      label: 'BLACK FRIDAY',
      labelFont: 'font-fugaz',
      Content: BlackFridayPromotionContent,
      date: '27.11 - 01.12',
      image: BlackFridayOpener,
      dateColor: 'text-primary',
    },
    christmas: {
      background: 'bg-gradient-to-r from-[#E31D1D] to-[#C11B2A]',
      label: 'Christmas Sale',
      labelFont: 'font-concert',
      Content: ChristmasPromotionContent,
      date: '25.12',
      image: ChristmasOpener,
      dateColor: 'text-white',
    },
    newYear: {
      background: 'bg-gradient-to-r from-[#F2C94C] to-[#F2994A]',
      label: 'Silvester-Kracher',
      labelFont: 'font-agbalumo',
      Content: NewYearPromotionContent,
      date: '27.12 - 02.01',
      image: NewYearOpener,
      dateColor: 'text-white',
    },
    cleverFox: {
      background: 'bg-gradient-to-r from-[#F2962A] to-[#FA7B34]',
      label: 'Sparfuchs-Special',
      labelFont: 'font-fugaz',
      Content: CleverFoxPromotionContent,
      date: '10.01 - 10.02',
      image: NewYearOpener,
      dateColor: 'text-white',
    },
    valentinesDay: {
      background: 'bg-gradient-to-r from-[#CC147C] to-[#F10956]',
      label: 'Herzenssache',
      labelFont: 'font-greatVibes',
      Content: ValentinesDayPromotionContent,
      date: '13.02 - 17.02',
      image: ValentinesDayOpener,
      dateColor: 'text-white',
    },
    easter: {
      background: 'bg-gradient-to-r from-[#F86548] to-[#EF236C]',
      label: 'Rabatte vom Osterhasen',
      labelFont: 'font-fugaz',
      Content: EasterPromotionContent,
      date: '01.04 - 08.04',
      image: EasterPromotionOpener,
      dateColor: 'text-white',
    },
    christmasAndNewYear: {
      background: 'bg-gradient-to-r from-[#E31D1D] to-[#C11B2A]',
      label: 'Weihnachts- & Silvester-Special',
      labelFont: 'font-concert',
      Content: ChristmasAndNewYearPromotionContent,
      date: '21.12 - 05.01',
      image: ChristmasOpener,
      dateColor: 'text-white',
    },
    newWarehouse: {
      background: 'bg-gradient-to-r from-[#E15501] to-[#E15501]',
      label: 'NEUERÖFFNUNG',
      labelFont: 'font-fugaz',
      Content: NewWarehousePromotionContent,
      date: 'Ab 25.03.2026',
      image: NewYearOpener,
      dateColor: 'text-white',
    },
    newWarehouseSwissbox: {
      background: 'bg-gradient-to-r from-[#6AB760] to-accent',
      label: 'NEUERÖFFNUNG',
      labelFont: 'font-fugaz',
      Content: NewSwissboxWarehousePromotionContent,
      date: 'Ab 01.06.2026',
      dateColor: 'text-white',
    },
  };

  const openSpecialPromotionModal = (): void => {
    openModal(eventThemeMap[eventTheme].Content);
  };

  if (!showSpecialPromotion) {
    return null;
  }

  return (
    <div
      className={`absolute z-50 left-0 right-0 top-0 text-center text-white cursor-pointer ${eventThemeMap[eventTheme].background}`}
      onClick={openSpecialPromotionModal}
    >
      <div className="relative flex justify-between items-center px-2 desktop:px-7 desktop:h-14 h-[82px]">
        <div className={`text-3xl desktop:text-4xl ${eventThemeMap[eventTheme].labelFont} z-[60]`}>
          {t(eventThemeMap[eventTheme].label)}
        </div>
        <div className="font-normal text-sm desktop:text-lg z-[90]">{t('We have discounts for you. Click to find out')}</div>
        <div className={`z-[10] text-lg desktop:text-2xl ${eventThemeMap[eventTheme].labelFont} ${eventThemeMap[eventTheme].dateColor}`}>
          {eventThemeMap[eventTheme].date}
        </div>
        {eventThemeMap[eventTheme].image ? (
          <img className="absolute right-0 h-full" src={eventThemeMap[eventTheme].image} alt="Promotion" />
        ) : null}
      </div>
    </div>
  );
});
